import React, { useState, useEffect, useCallback } from "react";
import { FormGroup, Form, Modal, ModalBody } from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import swal from "sweetalert";
import { Col, Button, Icon } from "../../Component";
import http from "../../../utils/http-common";
import ConfirmModal from "../../partials/user-manage/ConfirmModal";
import UserPicker from "../../partials/user-manage/UserPicker";
import CreatePartnerUserModal from "../../partials/user-manage/CreatePartnerUserModal";
import { createEmptyMedicalPartnerFormData } from "./medicalPartnerCreateModalDefaults";
import { contactIdentityPayloadFromUser } from "../../../utils/medicalPartnerPrimaryUser";
import PrimaryDirectoryUserIdentityPanel from "./PrimaryDirectoryUserIdentityPanel";
import {
  PARTNER_CONTACT_IDENTITY_HINT,
  PARTNER_TELECONSULTATION_CREATE_NOTE,
} from "./medicalPartnerAdminFormCopy";
import {
  PARTNER_TC_PLATFORM_SELECT_OPTIONS,
  tcApprovalApiStatusFromSelectValue,
  tcApprovalSelectValueFromPartner,
} from "./medicalPartnerAdminConstants";
import CountryCodeSelect from "../../common/CountryCodeSelect";
import { normalizeDialCodeDigits } from "../../../utils/dialCodeFieldNormalize";

const RHF_DEFAULTS = {
  organization_name: "",
  contact_person_country_code: "",
  contact_person_phone: "",
  contact_person_address: "",
  contact_person_city: "",
  contact_person_country: "",
  business_website: "",
  business_registration: "",
  remarks: "",
};

function MedicalPartnerCreateModal({
  isOpen,
  onClose,
  filterPrimaryUserCandidates,
  onPartnersSynced,
  onOpenDossier,
  onBusyChange,
}) {
  const [formExtras, setFormExtras] = useState(() => createEmptyMedicalPartnerFormData());
  const [primaryUser, setPrimaryUser] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { errors, register, handleSubmit, reset, getValues, control, setValue } = useForm({ defaultValues: RHF_DEFAULTS });

  const resetAll = useCallback(() => {
    reset(RHF_DEFAULTS);
    const empty = createEmptyMedicalPartnerFormData();
    setFormExtras(empty);
    setPrimaryUser(null);
    setSnapshot(
      JSON.stringify({
        ...empty,
        ...RHF_DEFAULTS,
        _snapshotPrimaryUserId: "",
      })
    );
  }, [reset]);

  useEffect(() => {
    if (!isOpen) {
      setDiscardOpen(false);
      setCreateUserOpen(false);
      return;
    }
    resetAll();
  }, [isOpen, resetAll]);

  /** Prefill operational country from directory user `source_country` when the field is still empty (aligns with API fallback). */
  useEffect(() => {
    if (!primaryUser?._id) return;
    const fromUser = String(primaryUser.source_country || "").trim();
    if (!fromUser) return;
    const cur = String(getValues("contact_person_country") || "").trim();
    if (cur) return;
    setValue("contact_person_country", fromUser, { shouldDirty: true });
  }, [primaryUser?._id, primaryUser?.source_country, getValues, setValue]);

  const isDirty = useCallback(() => {
    if (!snapshot) return false;
    const values = getValues();
    return (
      JSON.stringify({
        ...formExtras,
        ...values,
        _snapshotPrimaryUserId: primaryUser?._id || "",
      }) !== snapshot
    );
  }, [snapshot, formExtras, primaryUser, getValues]);

  const closeNow = useCallback(() => {
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (!isOpen) return;
    if (submitting) return;
    if (!snapshot || !isDirty()) {
      closeNow();
    } else {
      setDiscardOpen(true);
    }
  }, [isOpen, submitting, snapshot, isDirty, closeNow]);

  const onSubmit = (submitData) => {
    if (!formExtras.userId) {
      swal("Validation", "Select a primary user account first.", "warning");
      return;
    }

    const identity = contactIdentityPayloadFromUser(primaryUser);
    const email = (identity.contact_person_email || "").trim();
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      swal(
        "Validation",
        "The primary account must have a valid email in the directory. Update the user profile or pick another account.",
        "warning"
      );
      return;
    }

    const payload = {
      userId: submitData.userId || formExtras.userId,
      organization_name: submitData.organization_name,
      contact_person_name: identity.contact_person_name || "",
      contact_person_email: email,
      contact_person_phone: submitData.contact_person_phone,
      contact_person_country_code: normalizeDialCodeDigits(submitData.contact_person_country_code) || "",
      contact_person_address: submitData.contact_person_address,
      contact_person_city: submitData.contact_person_city,
      contact_person_country:
        String(submitData.contact_person_country || "").trim() ||
        String(primaryUser?.source_country || "").trim() ||
        "",
      business_website: submitData.business_website || "",
      business_registration: submitData.business_registration || "",
      organization_logo: submitData.organization_logo || "",
      offersMedicalTourism: formExtras.offersMedicalTourism === "yes",
      remarks: submitData.remarks || "",
      status: "pending",
      tcApprovalStatus: tcApprovalApiStatusFromSelectValue(formExtras.tcApprovalStatus || "off"),
    };

    setSubmitting(true);
    if (typeof onBusyChange === "function") onBusyChange(true);
    http
      .post("/api/v1/medical-partners", payload)
      .then((postResponse) => {
        const created = postResponse?.data?.data ?? postResponse?.data;
        const newId =
          (created && typeof created === "object" && (created._id || created.id)) ||
          postResponse?.data?._id ||
          null;
        return http.get("/api/v1/medical-partners").then((listResponse) => ({
          newId,
          listResponse,
          createdPayload: created,
        }));
      })
      .then(({ newId, listResponse, createdPayload }) => {
        const partners = listResponse.data?.data || listResponse.data || [];
        const cleanData = partners.filter((item) => item && item.organization_name);
        onPartnersSynced(cleanData);
        closeNow();
        if (newId) onOpenDossier(String(newId));
        const slug =
          createdPayload && typeof createdPayload === "object"
            ? createdPayload.landing_slug || createdPayload.slug
            : null;
        swal(
          "Success",
          slug ? `Partner created successfully. Public slug: ${slug}` : "Partner created successfully",
          "success"
        );
      })
      .catch((error) => {
        const msg = error.response?.data?.message || error.message || "Failed to create partner";
        swal("Error", msg, "error");
      })
      .finally(() => {
        setSubmitting(false);
        if (typeof onBusyChange === "function") onBusyChange(false);
      });
  };

  const busy = submitting;

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={requestClose}
        backdrop={busy ? "static" : true}
        keyboard={!busy}
        className="modal-dialog-centered"
        size="lg"
      >
        <ModalBody>
          <a
            href="#cancel"
            className="close"
            onClick={(ev) => {
              ev.preventDefault();
              requestClose();
            }}
          >
            <Icon name="cross-sm" />
          </a>
          <div className="p-2">
            <h5 className="title">New partner</h5>
            <p className="text-soft small mb-0">
              Step 1: select or create the primary user account. Then complete organization and contact — clinics and
              practitioners come next on the partner detail page.
            </p>
            <div className="mt-4">
              <Form className="row gy-4" onSubmit={handleSubmit(onSubmit)}>
                <Col md="12">
                  {!formExtras.userId ? (
                    <div className="alert alert-warning py-2 small" role="status">
                      <Icon name="alert-circle" className="me-1" />
                      Step 1: select or create the primary user account before filling organization details.
                    </div>
                  ) : null}
                </Col>
                <Col md="12">
                  <FormGroup>
                    <label className="form-label" htmlFor="add-partner-primary-user">
                      Primary user account *
                    </label>
                    <UserPicker
                      idPrefix="add-partner-primary-user"
                      value={primaryUser}
                      onChange={(u) => {
                        setPrimaryUser(u);
                        const id = u && u._id ? u._id : "";
                        setFormExtras((prev) => ({ ...prev, userId: id }));
                      }}
                      disabled={busy}
                      filterCandidate={filterPrimaryUserCandidates}
                      onRequestCreateAccount={() => setCreateUserOpen(true)}
                      emptyHint="No users found — create an account with the role medical_partner"
                    />
                    <div className="mt-2">
                      <small className="text-soft d-block">
                        <Icon name="info" className="me-1" />
                        One directory user can be the primary login for only one partner. Users already linked as primary
                        are hidden. The partner&apos;s contact name and email are
                        taken from this account — they cannot be typed differently here (avoids duplicate identities).
                        On create, the role is set to <code>medical_partner</code> (admins keep <code>admin</code>).
                      </small>
                    </div>
                  </FormGroup>
                </Col>

                <Col md="12" className="p-0">
                  <div
                    className="row g-gs gy-4 w-100 m-0"
                    style={{
                      opacity: formExtras.userId ? 1 : 0.55,
                      pointerEvents: formExtras.userId ? "auto" : "none",
                    }}
                    aria-hidden={!formExtras.userId}
                  >
                    <Col md="12">
                      <h6 className="overline-title">Organization Details</h6>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-label">Organization Name *</label>
                        <input
                          className="form-control"
                          type="text"
                          name="organization_name"
                          placeholder="Enter organization name"
                          ref={register({ required: "This field is required" })}
                        />
                        {errors.organization_name ? (
                          <span className="invalid">{errors.organization_name.message}</span>
                        ) : null}
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <h6 className="overline-title">Contact identity (from primary account)</h6>
                      <p className="text-soft small mb-2">{PARTNER_CONTACT_IDENTITY_HINT}</p>
                    </Col>
                    <Col md="12">
                      <PrimaryDirectoryUserIdentityPanel user={primaryUser} />
                    </Col>
                    <Col md="12">
                      <h6 className="overline-title">Operational phone &amp; address</h6>
                      <p className="text-soft small mb-2">
                        Optional reachability fields for the partner record (can differ from the user profile). Per-clinic
                        offers are configured later under <strong>Clinics</strong>.
                      </p>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="new-partner-dial-code">
                          Country calling code
                        </label>
                        <Controller
                          name="contact_person_country_code"
                          control={control}
                          render={({ value, onChange }) => (
                            <CountryCodeSelect
                              id="new-partner-dial-code"
                              value={value}
                              onChange={onChange}
                              disabled={busy}
                            />
                          )}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Phone Number</label>
                        <input
                          className="form-control"
                          type="text"
                          name="contact_person_phone"
                          placeholder="Enter phone number"
                          ref={register}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <FormGroup>
                        <label className="form-label">Address</label>
                        <input
                          className="form-control"
                          type="text"
                          name="contact_person_address"
                          placeholder="Enter address"
                          ref={register}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <label className="form-label">City</label>
                        <input
                          className="form-control"
                          type="text"
                          name="contact_person_city"
                          placeholder="Enter city"
                          ref={register}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <label className="form-label">Country</label>
                        <input
                          className="form-control"
                          type="text"
                          name="contact_person_country"
                          placeholder="Enter country"
                          ref={register}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <h6 className="overline-title">Business Details</h6>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Business Website</label>
                        <input
                          className="form-control"
                          type="text"
                          name="business_website"
                          placeholder="Enter website URL"
                          ref={register()}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label">Business Registration</label>
                        <input
                          className="form-control"
                          type="text"
                          name="business_registration"
                          placeholder="Enter registration number"
                          ref={register()}
                        />
                      </FormGroup>
                    </Col>

                    <Col md="12">
                      <h6 className="overline-title">Programs &amp; platform</h6>
                      <p className="text-soft small mb-3">{PARTNER_TELECONSULTATION_CREATE_NOTE}</p>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <label className="form-label" htmlFor="new-partner-tc-platform">
                          TC platform
                        </label>
                        <select
                          id="new-partner-tc-platform"
                          className="form-control"
                          disabled={busy}
                          value={tcApprovalSelectValueFromPartner(formExtras)}
                          onChange={(e) => {
                            const next = tcApprovalApiStatusFromSelectValue(e.target.value);
                            setFormExtras((p) => ({ ...p, tcApprovalStatus: next }));
                          }}
                        >
                          {PARTNER_TC_PLATFORM_SELECT_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <small className="text-soft d-block mt-1">Admin-only field. Clinic TC channels: dossier → Teleconsultation.</small>
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <div className="custom-control custom-switch mb-3">
                        <input
                          type="checkbox"
                          className="custom-control-input"
                          id="offersMedicalTourism"
                          checked={formExtras.offersMedicalTourism === "yes"}
                          onChange={(e) =>
                            setFormExtras((p) => ({ ...p, offersMedicalTourism: e.target.checked ? "yes" : "no" }))
                          }
                        />
                        <label className="custom-control-label" htmlFor="offersMedicalTourism">
                          Medical tourism program (org participates in travel-assisted / packaged care)
                        </label>
                      </div>
                      <p className="text-soft small mb-2">
                        Turn off only for partners who will use <strong>teleconsultation only</strong> with no MT packaging.
                      </p>
                      <p className="text-soft small mb-0">
                        Hotel, accommodation, and translator: set on each <strong>clinic</strong> after creation.
                      </p>
                    </Col>

                    <Col md="12">
                      <h6 className="overline-title">Additional Information</h6>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <label className="form-label">Remarks</label>
                        <textarea
                          className="form-control"
                          name="remarks"
                          placeholder="Enter remarks"
                          rows="3"
                          ref={register()}
                        />
                      </FormGroup>
                    </Col>
                  </div>
                </Col>

                <Col md="12">
                  <div className="alert alert-info">
                    <Icon name="info" className="me-2" />
                    <strong>Important:</strong> New partners are created with status <strong>&quot;Pending&quot;</strong>.
                    A default clinic is created automatically from the partner information. You must approve the partner
                    manually to activate them.
                  </div>
                </Col>

                <Col size="12">
                  {busy ? (
                    <div
                      className="overlay-loading"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255,255,255,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                        borderRadius: "8px",
                      }}
                    >
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : null}
                  <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                    <li>
                      <Button
                        color="primary"
                        size="md"
                        type="submit"
                        disabled={busy || !formExtras.userId}
                      >
                        {busy ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            Creating...
                          </>
                        ) : (
                          "Add partner"
                        )}
                      </Button>
                    </li>
                    <li>
                      <a
                        href="#cancel"
                        className="link link-light"
                        onClick={(ev) => {
                          ev.preventDefault();
                          requestClose();
                        }}
                      >
                        Cancel
                      </a>
                    </li>
                  </ul>
                </Col>
              </Form>
            </div>
          </div>
        </ModalBody>
      </Modal>

      <CreatePartnerUserModal
        isOpen={createUserOpen}
        onClose={() => setCreateUserOpen(false)}
        onCreated={(u) => {
          setPrimaryUser(u);
          setFormExtras((prev) => ({ ...prev, userId: u._id }));
          setCreateUserOpen(false);
        }}
      />

      <ConfirmModal
        isOpen={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          closeNow();
        }}
        title="Discard unsaved changes?"
        message="You will lose edits made in this form."
        confirmText="Discard"
        cancelText="Keep editing"
        type="warning"
        loading={false}
      />
    </>
  );
}

export default MedicalPartnerCreateModal;
